import { useEffect, useId, useRef, useState } from 'react';
import { GOAL_TITLE, type Goal } from '@jungles/shared';
import { GOAL_OPTIONS } from '../lib/goal-options';
import './goal-picker.css';

export function GoalPicker({ value, onChange }: { value: Goal; onChange: (goal: Goal) => void }) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const options = useRef<(HTMLButtonElement | null)[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const selected = GOAL_OPTIONS.find(item => item.id === value)!;
  const pointerPending = useRef(false);
  const close = () => { setOpen(false); trigger.current?.focus({ preventScroll: true }); };
  const show = (index = GOAL_OPTIONS.findIndex(item => item.id === value)) => { setActive(index); setOpen(true); };
  useEffect(() => {
    if (!open) return;
    options.current[active]?.focus({ preventScroll: true });
    options.current[active]?.scrollIntoView({ block: 'nearest' });
  }, [open, active]);
  useEffect(() => {
    if (open) document.getElementById(id)?.scrollIntoView({ block: 'nearest' });
  }, [open, id]);
  useEffect(() => {
    if (!open) return;
    // Keep the DOM stable through touch → blur → compatibility click. Closing
    // on pointerdown can move another action under the finger; blur can make
    // the trigger's delayed click reopen a picker that was just dismissed.
    const start = () => { pointerPending.current = true; };
    const cancel = () => { pointerPending.current = false; };
    const click = (event: MouseEvent) => {
      pointerPending.current = false;
      if (!root.current?.contains(event.target as Node)) {
        event.preventDefault(); event.stopImmediatePropagation();
        close();
      }
    };
    document.addEventListener('pointerdown', start, true);
    document.addEventListener('pointercancel', cancel, true);
    document.addEventListener('click', click, true);
    return () => {
      pointerPending.current = false;
      document.removeEventListener('pointerdown', start, true);
      document.removeEventListener('pointercancel', cancel, true);
      document.removeEventListener('click', click, true);
    };
  }, [open]);
  return <div ref={root} className="goal-picker" onBlur={event => {
    if (open && !pointerPending.current && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
  }}>
    <span className="goal-picker-label" id={`${id}-label`}>Цель</span>
    <button ref={trigger} className="goal-trigger" type="button" aria-label={`Цель: ${GOAL_TITLE[value]}`} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => open ? close() : show()} onKeyDown={event => {
        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
          event.preventDefault(); show(event.key === 'Home' ? 0 : event.key === 'End' ? GOAL_OPTIONS.length - 1 : GOAL_OPTIONS.findIndex(item => item.id === value));
        }
      }}>
      <img src={selected.icon} alt="" /><span><b>{GOAL_TITLE[value]}</b><small>{selected.hint}</small></span><span className="goal-chevron" aria-hidden="true">{open ? '▴' : '▾'}</span>
    </button>
    {open ? <div id={id} role="listbox" aria-labelledby={`${id}-label`} className="goal-options" onKeyDown={event => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
      if (['Home', 'End', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
        event.preventDefault();
        setActive(index => event.key === 'Home' ? 0 : event.key === 'End' ? GOAL_OPTIONS.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + GOAL_OPTIONS.length) % GOAL_OPTIONS.length);
      }
    }}>{GOAL_OPTIONS.map((item, index) => <button key={item.id} ref={element => { options.current[index] = element; }} role="option" aria-selected={value === item.id} tabIndex={active === index ? 0 : -1} type="button"
      onClick={() => { if (item.id !== value) onChange(item.id); close(); }}>
      <img src={item.icon} alt="" /><span><b>{GOAL_TITLE[item.id]}</b><small>{item.hint}</small></span>{item.id === value ? <span className="goal-check" aria-hidden="true">✓</span> : null}
    </button>)}</div> : null}
  </div>;
}
